import { useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { usePermisos } from '../context/PermisosContext';
import { useAuth } from '../context/AuthContext';

/**
 * Hook que hace polling a /api/parametros/alertas cada 30s.
 * Si hay alertas NUEVAS (por id), muestra una notificación del navegador
 * y devuelve la lista actual.
 *
 * Solo activo para biólogo/admin/super_admin.
 */
export function useAlertasPush() {
  const { usuario } = useAuth();
  const { tienePermiso } = usePermisos();
  const [alertas, setAlertas] = useState([]);
  const alertasSonadas = useRef(new Set());

  const activo =
    usuario &&
    ['biologo', 'admin', 'super_admin'].includes(usuario.rol) &&
    tienePermiso('operacion.parametros');

  useEffect(() => {
    if (!activo) return;
    if (!('Notification' in window)) return;

    // Pedir permiso al navegador (una sola vez)
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    let cancelado = false;

    const consultar = async () => {
      try {
        const { data } = await api.get('/parametros/alertas');
        if (cancelado) return;
        const lista = data.alertas || [];

        // Detectar alertas nuevas
        const nuevas = lista.filter((a) => !alertasSonadas.current.has(a.id));

        if (nuevas.length > 0 && Notification.permission === 'granted') {
          for (const a of nuevas.slice(0, 3)) {
            const n = new Notification('⚠️ Alerta de parámetros', {
              body: `${a.piscina_codigo} · ${a.sector_nombre}\n${a.mensaje}`,
              tag: a.id,
              requireInteraction: a.nivel === 1,
            });
            setTimeout(() => n.close(), 15000);
          }
        }

        // Marcar todas como sonadas
        for (const a of lista) alertasSonadas.current.add(a.id);

        setAlertas(lista);
      } catch {
        // Silencioso
      }
    };

    consultar();
    const timer = setInterval(consultar, 30000);

    return () => {
      cancelado = true;
      clearInterval(timer);
    };
  }, [activo]);

  return alertas;
}